import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowLeft, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = '/api';

// Shipping calculation
const calculateShipping = (country, totalItems) => {
  if (country === 'CY') {
    return 0; // Free shipping in Cyprus
  } else if (country === 'GR') {
    // Greece: €10 per bottle, max €35
    if (totalItems >= 4) return 35;
    return totalItems * 10;
  }
  return 0;
};

export default function Cart() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, updateQuantity, removeFromCart, loading, sessionId } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [shippingCountry, setShippingCountry] = useState('CY');

  useEffect(() => {
    document.title = 'Cart - Salt & Stone';
  }, []);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const shippingCost = calculateShipping(shippingCountry, totalItems);
  const orderTotal = cartTotal + shippingCost;

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        session_id: sessionId,
        origin_url: window.location.origin,
        shipping_country: shippingCountry,
        shipping_cost: shippingCost
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="cart-page">
      {/* Header */}
      <section className="py-16 md:py-24 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link 
            to="/shop" 
            className="inline-flex items-center gap-2 text-[#F2F0E9]/60 hover:text-[#D4AF37] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>
          
          <div className="text-center">
            <span className="caption block mb-6">Your Selection</span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#F2F0E9] mb-4">
              Cart
            </h1>
            <div className="w-20 h-px bg-[#D4AF37] mx-auto"></div>
          </div>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {cartItems.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-16 h-16 text-[#F2F0E9]/20 mx-auto mb-6" />
              <p className="text-[#F2F0E9]/60 mb-8">Your cart is empty</p>
              <Link to="/shop" className="btn-primary">
                Explore Our Collection
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item flex gap-6" data-testid={`cart-item-${item.id}`}>
                    {/* Image */}
                    <div className="w-24 h-24 bg-[#0A0A0A] flex-shrink-0 overflow-hidden">
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-grow">
                      <h3 className="font-['Playfair_Display'] text-lg text-[#F2F0E9] mb-2">
                        {item.product_name}
                      </h3>
                      <p className="text-[#D4AF37] text-sm mb-4">€{item.price.toFixed(2)}</p>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={loading}
                            className="quantity-btn"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center text-[#F2F0E9]" data-testid={`qty-${item.id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            disabled={loading}
                            className="quantity-btn"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item.id)}
                          disabled={loading}
                          className="text-[#F2F0E9]/40 hover:text-red-500 transition-colors"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Subtotal */}
                    <div className="text-right">
                      <span className="font-['Playfair_Display'] text-lg text-[#F2F0E9]">
                        €{(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="glass p-8 sticky top-32">
                  <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-6">
                    Order Summary
                  </h3>

                  {/* Shipping Country */}
                  <div className="mb-6">
                    <label className="block text-[10px] tracking-[0.15em] text-[#D4AF37] uppercase mb-2">
                      Ship to
                    </label>
                    <select
                      value={shippingCountry}
                      onChange={(e) => setShippingCountry(e.target.value)}
                      className="w-full bg-transparent border border-white/20 text-[#F2F0E9] px-4 py-3 rounded-none focus:border-[#D4AF37] outline-none transition-colors"
                      data-testid="shipping-country"
                    >
                      <option value="CY" className="bg-[#050505]">Cyprus</option>
                      <option value="GR" className="bg-[#050505]">Greece</option>
                    </select>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F2F0E9]/60">Subtotal</span>
                      <span className="text-[#F2F0E9]">€{cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#F2F0E9]/60">Shipping</span>
                      <span className="text-[#F2F0E9]">
                        {shippingCost === 0 ? 'Free' : `€${shippingCost.toFixed(2)}`}
                      </span>
                    </div>
                    {shippingCountry === 'GR' && totalItems >= 4 && (
                      <p className="text-[#D4AF37]/70 text-xs">
                        Flat rate shipping for 4+ bottles
                      </p>
                    )}
                  </div>

                  <div className="w-full h-px bg-white/10 mb-6"></div>

                  <div className="flex justify-between mb-8">
                    <span className="text-[#F2F0E9]">Total</span>
                    <span className="font-['Playfair_Display'] text-2xl text-[#F2F0E9]" data-testid="cart-total">
                      €{orderTotal.toFixed(2)}
                    </span>
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={checkoutLoading || cartItems.length === 0}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="checkout-btn"
                  >
                    {checkoutLoading ? (
                      <div className="spinner w-5 h-5"></div>
                    ) : (
                      'Proceed to Checkout'
                    )}
                  </button>

                  <p className="text-[#F2F0E9]/30 text-xs text-center mt-6">
                    Secure checkout powered by Stripe
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Trust Badges */}
      {cartItems.length > 0 && (
        <section className="py-16 px-6 border-t border-white/5">
          <div className="container mx-auto max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <h4 className="text-[#F2F0E9] text-sm font-medium mb-2">Secure Payment</h4>
                <p className="text-[#F2F0E9]/50 text-xs">256-bit SSL encryption</p>
              </div>
              <div>
                <h4 className="text-[#F2F0E9] text-sm font-medium mb-2">Premium Packaging</h4>
                <p className="text-[#F2F0E9]/50 text-xs">Gift-ready presentation</p>
              </div>
              <div>
                <h4 className="text-[#F2F0E9] text-sm font-medium mb-2">Quality Guaranteed</h4>
                <p className="text-[#F2F0E9]/50 text-xs">Satisfaction promise</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
